/**
 * Heartbeat xem video → event log học tập (TT 83 C2-c).
 *
 * `WatchSegmentTracker` gom các đoạn [start, end) đã THỰC xem: thời gian chạy liên tục
 * (bước ≤ MAX_STEP giây) nối dài đoạn hiện tại; tua/tạm dừng lâu → đóng đoạn, mở đoạn mới.
 * `useVideoHeartbeat` gửi gộp mỗi FLUSH_MS + khi rời bài/ẩn tab tới
 * `POST /api/analytics/video-heartbeat/` (BE hợp nhất đoạn → % đã xem thực).
 *
 * Chưa làm (D-C2-1): hàng đợi offline IndexedDB — mất mạng thì đoạn của lần gửi đó bị bỏ.
 */
import { useEffect, useRef } from "react";

import { apiClient } from "@/api/client";

const MAX_STEP = 2.5;
const MAX_SEGMENTS = 20;
const FLUSH_MS = 20_000;

export type Segment = [number, number];

export class WatchSegmentTracker {
  private segments: Segment[] = [];
  private start: number | null = null;
  private last: number | null = null;

  feed(t: number): void {
    if (this.last !== null && t > this.last && t - this.last <= MAX_STEP) {
      this.last = t;
      return;
    }
    this.close();
    this.start = t;
    this.last = t;
  }

  /** Lấy các đoạn đã xem (giây nguyên) và bắt đầu gom lại từ vị trí hiện tại. */
  drain(): Segment[] {
    const resume = this.last;
    this.close();
    if (resume !== null) {
      this.start = resume;
      this.last = resume;
    }
    const out = this.segments.splice(0, this.segments.length);
    return out.slice(-MAX_SEGMENTS);
  }

  private close(): void {
    if (this.start !== null && this.last !== null) {
      const s = Math.floor(this.start);
      const e = Math.floor(this.last);
      if (e > s) this.segments.push([s, e]);
    }
    this.start = null;
    this.last = null;
  }
}

function newUuid(): string | undefined {
  return typeof crypto !== "undefined" && "randomUUID" in crypto ? crypto.randomUUID() : undefined;
}

export async function sendHeartbeat(lessonId: number, segments: Segment[]): Promise<void> {
  if (segments.length === 0) return;
  await apiClient.post("/analytics/video-heartbeat/", {
    lesson_id: lessonId,
    segments,
    event_uuid: newUuid(),
  });
}

/** Trả hàm `onTime(currentTime)` để gắn vào `VideoPlayer.onTimeUpdate`. */
export function useVideoHeartbeat(lessonId: number | undefined) {
  const tracker = useRef(new WatchSegmentTracker());

  useEffect(() => {
    if (!lessonId) return;
    const current = tracker.current;
    const flush = () => {
      sendHeartbeat(lessonId, current.drain()).catch(() => undefined);
    };
    const timer = setInterval(flush, FLUSH_MS);
    const onHide = () => {
      if (document.visibilityState === "hidden") flush();
    };
    document.addEventListener("visibilitychange", onHide);
    return () => {
      clearInterval(timer);
      document.removeEventListener("visibilitychange", onHide);
      flush();
      tracker.current = new WatchSegmentTracker();
    };
  }, [lessonId]);

  return (currentTime: number) => tracker.current.feed(currentTime);
}
